import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import {
  User as UserIcon, ChevronDown, X, LogOut, BookUser, Receipt, Bell,
  Plug, Code, BadgeCheck, AlertTriangle, Headphones, LayoutDashboard
} from "lucide-react";
import Avatar from "@/components/Avatar";

// Mismo menú de usuario que la plataforma (Layout): avatar + dropdown con
// Mi Perfil, navegación de cuenta y cerrar sesión. Se usa en el landing para
// visitantes autenticados, sincronizado con el menú de la plataforma.
const ACCOUNT_NAV = [
  { to: "/libreta-direcciones", label: "Direcciones", icon: BookUser },
  { to: "/facturacion", label: "Facturación", icon: Receipt },
  { to: "/notificaciones", label: "Notificaciones", icon: Bell },
  { to: "/integraciones", label: "Integraciones", icon: Plug },
  { to: "/api-bhgc", label: "API BHGC", icon: Code },
  { to: "/verificacion-identidad", label: "Verificación", icon: BadgeCheck },
  { to: "/reclamaciones", label: "Reclamaciones", icon: AlertTriangle },
  { to: "/soporte", label: "Soporte", icon: Headphones }
];

export default function UserMenu() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 1023px)").matches : false
  );

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 1023px)");
    const onChange = () => setIsMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (!user) return null;
  const isActive = (to) => location.pathname.startsWith(to);

  const trigger = (
    <button onClick={() => setOpen(v => !v)} className="flex items-center gap-2 surface-inset px-2 py-1.5">
      <Avatar user={user} size={28} />
      <ChevronDown className="w-3.5 h-3.5 text-ink-muted" />
    </button>
  );

  const links = (
    <>
      <Link to="/panel" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-white/5">
        <LayoutDashboard className="w-4 h-4" /> Ir al Panel
      </Link>
      <Link to="/perfil" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-white/5">
        <UserIcon className="w-4 h-4" /> Mi Perfil
      </Link>
      <div className="border-t border-ink-border/60">
        {ACCOUNT_NAV.map(n => {
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className={`flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-white/5 ${isActive(n.to) ? "text-cyan" : ""}`}>
              <Icon className="w-4 h-4" /> {n.label}
            </Link>
          );
        })}
      </div>
      <button onClick={() => { setOpen(false); logout(); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-white/5 text-destructive border-t border-ink-border">
        <LogOut className="w-4 h-4" /> Cerrar sesión
      </button>
    </>
  );

  return (
    <div className="relative">
      {trigger}
      {open && !isMobile && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-60 surface z-40">
            <div className="px-4 py-3 border-b border-ink-border">
              <div className="text-sm font-medium truncate">{user?.full_name || "Usuario"}</div>
              <div className="text-xs text-ink-muted truncate">{user?.email}</div>
            </div>
            {links}
          </div>
        </>
      )}
      {open && isMobile && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-ink/85 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="relative w-full max-w-sm bg-ink-surface border border-cyan/50 shadow-2xl text-ink-text max-h-[88dvh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-ink-border">
              <div className="leading-tight min-w-0">
                <div className="text-sm font-medium truncate">{user?.full_name || "Usuario"}</div>
                <div className="text-xs text-ink-muted truncate">{user?.email}</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink-text p-1 shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto overscroll-contain">{links}</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}